<script setup>
import BreadCrumbs from '@/components/breadcrumbs/BreadCrumbs.vue'
import ContentSubheaderPage from '@/components/content/ContentSubheaderPage.vue'
import VDatePicker from '@/components/v-calendar/VDatePicker.vue'
import SearchField from '@/components/fields/SearchField.vue'
import { ref, onMounted, onBeforeUpdate, watch } from 'vue'
import NewsCard from '@/components/news/NewsCard.vue'
import { useNewsStore, newsDateRangeParams } from '@/store/news'
import * as helper from '@/store/helpers'

const newsStore = useNewsStore()

const searchStr = ref('')
const dates = ref({ start: null, end: null })
const newsArr = ref([])
const newsCount = ref(0)
const newsCategories = ref([])
const selectedCategories = ref([])

const pageNumber = ref(1) // Текущая страница
const paginationCount = ref(16) // Количество новостей на 1 странице
const maxPage = ref(0)
const pageElems = ref([]) // ref-ссылки на dom-элементы (номера страниц)

const isNewsDataValid = (newsData) => {
    if (
        newsData.text_header === null ||
        newsData.text_header.length === 0 ||
        newsData.creation_time === null
    ) {
        return false
    }

    return true
}

const getMaxPage = (newsCount) => {
    return Math.ceil(newsCount / paginationCount.value)
}

function setNewsFromStore() {
    newsArr.value = newsStore.allNews.filter(isNewsDataValid)
}

const changePage = (page) => {
    pageNumber.value = page

    for (let i = 0; i < pageElems.value.length; i++) {
        if (pageElems.value[i].classList.contains('toggle')) {
            pageElems.value[i].classList.remove('toggle')
            break
        }
    }

    if (pageElems.value.length > 1) {
        pageElems.value[page - 1].classList.add('toggle')
    }

    document
        .getElementById('all-news-page')
        .scrollIntoView({ behavior: 'smooth' }) // Прокрутка страницы
}

async function clearFilters() {
    dates.value = { start: null, end: null }
    searchStr.value = ''
    selectedCategories.value = []

    newsCount.value = await helper
        .api('GET', '/news_with_conferences_and_seminars/count', {})
        .then((res) => res)
    await newsStore.getNews(0, paginationCount.value * pageNumber.value)

    setNewsFromStore()
    maxPage.value = getMaxPage(newsCount.value)
    changePage(1)
}

async function searchNews() {
    newsCount.value = await helper
        .api('GET', '/news_with_conferences_and_seminars/count', {
            search_string: searchStr.value,
            dates: newsDateRangeParams(
                dates.value.start,
                dates.value.end
            ),
            categories_id: selectedCategories.value
        })
        .then((res) => res)
    await newsStore.getNews(
        0,
        newsCount.value,
        searchStr.value,
        dates.value.start !== null ? [dates.value.start, dates.value.end] : [],
        selectedCategories.value
    )

    setNewsFromStore()
    maxPage.value = getMaxPage(newsCount.value)
    changePage(1)
}

// Отслеживание переключения страниц
watch(pageNumber, async () => {
    newsCount.value = await helper
        .api('GET', '/news_with_conferences_and_seminars/count', {
            search_string: searchStr.value,
            dates: newsDateRangeParams(
                dates.value.start,
                dates.value.end
            ),
            categories_id: selectedCategories.value
        })
        .then((res) => res)
    await newsStore.getNews(
        paginationCount.value * (pageNumber.value - 1),
        paginationCount.value * pageNumber.value,
        searchStr.value,
        dates.value.start !== null ? [dates.value.start, dates.value.end] : [],
        selectedCategories.value
    )

    setNewsFromStore()
})

watch(dates, () => {
    if (!dates.value) {
        dates.value = { start: null, end: null }
    }
})

onMounted(async () => {
    newsCount.value = await helper
        .api('GET', '/news_with_conferences_and_seminars/count', {})
        .then((res) => res)
    await newsStore.getNews(0, paginationCount.value * pageNumber.value)
    await newsStore.getNewsCategories()

    setNewsFromStore()
    newsCategories.value = newsStore.newsCategories
    maxPage.value = getMaxPage(newsCount.value)
})

onBeforeUpdate(() => {
    pageElems.value = []
})
</script>

<template>
    <div
        id="all-news-page"
        class="container"
    >
        <bread-crumbs />
        <content-subheader-page
            class="all-news-page-content-subheader"
            text="НОВОСТИ АССОЦИАЦИИ"
        />
        <v-container
            :fluid="true"
            class="pa-0 inputs"
        >
            <v-row class="ml-n2 mr-n2">
                <v-col
                    class="pl-2 pr-2"
                    cols="12"
                    lg="6"
                >
                    <search-field v-model="searchStr" />
                </v-col>
                <v-col
                    class="pl-2 pr-2"
                    cols="12"
                    sm="6"
                    lg="3"
                >
                    <v-select
                        v-model="selectedCategories"
                        :items="newsCategories"
                        label="Все категории"
                        variant="outlined"
                        item-title="name"
                        item-value="id"
                        multiple
                    />
                </v-col>
                <v-col
                    class="pl-2 pr-2"
                    cols="12"
                    sm="6"
                    lg="3"
                >
                    <v-date-picker v-model.range="dates" />
                </v-col>
                <v-col
                    class="pl-2 pr-2"
                    cols="12"
                    sm="6"
                    lg="3"
                >
                    <v-btn
                        class="text-none inputs-button"
                        height="60"
                        variant="flat"
                        color="#3B66D3"
                        @click="clearFilters"
                    >
                        Сбросить фильтры
                    </v-btn>
                </v-col>
                <v-col
                    class="pl-2 pr-2 mt-5 mt-sm-0"
                    cols="12"
                    sm="6"
                    lg="3"
                >
                    <v-btn
                        class="text-none inputs-button"
                        height="60"
                        variant="flat"
                        color="#3B66D3"
                        @click="searchNews"
                    >
                        Поиск
                    </v-btn>
                </v-col>
            </v-row>
        </v-container>
        <v-container
            :fluid="true"
            class="pa-0 news-section"
        >
            <v-row class="ml-n2 mr-n2">
                <v-col
                    v-for="(news, index) in newsArr"
                    :key="index"
                    class="pl-2 pr-2 pb-2 pt-2"
                    cols="12"
                    sm="6"
                    lg="3"
                >
                    <news-card
                        :news-id="news.news_id"
                        :conference-id="news.conference_seminar_id"
                        :url="`/${news.thumbnail}`"
                        :title="news.text_header"
                        :date="
                            new Date(news.creation_time).toLocaleDateString(
                                'ru-RU',
                                { day: 'numeric', month: 'long' }
                            )
                        "
                        :is-pinned="
                            news.time_of_pin &&
                            new Date() < new Date(news.time_of_pin)
                        "
                    />
                </v-col>
            </v-row>
        </v-container>
        <v-container
            :fluid="true"
            class="pa-0 pagination"
            :class="{ 'd-none': maxPage < 2 }"
        >
            <div
                class="page-arrows"
                @click="changePage(1)"
            >
                <svg
                    width="20"
                    height="15"
                    viewBox="0 0 20 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M10 1.75152L8.48329 0.5L0 7.5L8.48329 14.5L10 13.2485L3.03342 7.5L10 1.75152Z"
                        fill="#0076DE"
                    />
                    <path
                        d="M20 1.75152L18.4833 0.5L10 7.5L18.4833 14.5L20 13.2485L13.0334 7.5L20 1.75152Z"
                        fill="#0076DE"
                    />
                </svg>
            </div>
            <div
                v-for="(page, i) in maxPage"
                v-show="
                    (pageNumber < 5 ? page <= 5 : page < pageNumber + 3) &&
                    (pageNumber < 5 ? true : page > pageNumber - 3)
                "
                :key="page"
                :ref="
                    (el) => {
                        if (el) pageElems[i] = el
                    }
                "
                class="page"
                :class="{ toggle: i === 0 }"
                @click="changePage(page)"
            >
                {{ page }}
            </div>
            <div
                class="page-arrows"
                @click="changePage(maxPage)"
            >
                <svg
                    width="20"
                    height="15"
                    viewBox="0 0 20 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        d="M10 13.2485L11.5167 14.5L20 7.5L11.5167 0.500001L10 1.75152L16.9666 7.5L10 13.2485Z"
                        fill="#0076DE"
                    />
                    <path
                        d="M0 13.2485L1.51671 14.5L10 7.5L1.51671 0.5L0 1.75152L6.96658 7.5L0 13.2485Z"
                        fill="#0076DE"
                    />
                </svg>
            </div>
        </v-container>
    </div>
</template>

<style lang="scss" scoped>
.container {
    margin-top: 32px;
    margin-left: 104px;
    margin-right: 104px;

    @media screen and (max-width: 767px) {
        margin-left: 40px;
        margin-right: 40px;
        margin-top: 24px;
    }
}

.all-news-page-content-subheader {
    text-align: left;
}

.v-col {
    padding: 0;
}

.inputs {
    margin-top: 32px;
}

.inputs-button {
    width: 100%;
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
}

.news-section {
    margin-top: 27px;
}

.pagination {
    display: flex;
    gap: 32px;
    align-items: center;
    justify-content: center;
    margin-top: 28px;

    .page-arrows {
        cursor: pointer;
    }

    .page {
        color: #0076de;
        font-size: 20px;
        line-height: 28px;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }

    .toggle {
        text-decoration: underline;
    }
}

:deep(.v-label) {
    font-weight: 500;
    font-size: 20px;
    line-height: 28px;
    color: #002866;
}
</style>
